import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError, downloadBlob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarDays, Printer, ChevronLeft, ChevronRight, Plus, FileDown, PillBottle, Info, AlertTriangle, CheckCircle2, MinusCircle, ShieldAlert, MoreVertical, Circle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { asArray, getAllergySeverity, ALLERGY_SEVERITY_STYLES } from "@/lib/clientFields";
import MobileAgendaView from "@/components/mobile/MobileAgendaView";

// Action metadata — text label + icon + colour so colour is never the sole
// signal (WCAG). Enum values (`administered` / `refused` / `withheld` / `prn`)
// are unchanged and match the backend's action enum exactly.
const ACTION_META = {
  administered: { label: "Administered", Icon: CheckCircle2, dot: "bg-emerald-500", ring: "border-emerald-300", text: "text-emerald-800" },
  refused:      { label: "Refused",      Icon: AlertTriangle, dot: "bg-amber-500",   ring: "border-amber-300",   text: "text-amber-800"   },
  withheld:     { label: "Withheld",     Icon: MinusCircle,   dot: "bg-slate-500",   ring: "border-slate-300",   text: "text-slate-800"   },
  prn:          { label: "PRN",          Icon: PillBottle,    dot: "bg-sky-500",     ring: "border-sky-300",     text: "text-sky-800"     },
};

function mondayOf(dateStr) {
  const d = dateStr ? new Date(dateStr + "T00:00:00") : new Date();
  const day = (d.getDay() + 6) % 7; // Mon=0..Sun=6
  d.setDate(d.getDate() - day);
  return d.toISOString().slice(0, 10);
}
function addDays(iso, n) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}
// Presentation-only: derive a human elapsed label from an ISO timestamp.
// Reads only the existing eMAR `given_at` field — no new clinical rule.
function elapsedSince(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return null;
  const min = Math.floor(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  const rm = min % 60;
  if (hr < 24) return rm ? `${hr}h ${rm}m ago` : `${hr}h ago`;
  const d = Math.floor(hr / 24);
  return `${d} day${d === 1 ? "" : "s"} ago`;
}
const ACTION_STYLE = {
  administered: "bg-emerald-100 text-emerald-800 border-emerald-300",
  refused:      "bg-amber-100 text-amber-800 border-amber-300",
  withheld:     "bg-slate-200 text-slate-800 border-slate-300",
  prn:          "bg-sky-100 text-sky-800 border-sky-300",
};
const ACTION_SHORT = { administered: "✓", refused: "R", withheld: "W", prn: "PRN" };

export default function MarChart() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [weekStart, setWeekStart] = useState(mondayOf());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("scheduled");
  const printRef = useRef();
  const [quickOpen, setQuickOpen] = useState(false);
  const [quick, setQuick] = useState({ med: null, day: "", action: "administered", reason: "", route: "", outcome: "", signature_name: "", witness_signature_name: "", witness_email: "", witness_password: "", witness_user_id: "", witness_verified: false });
  const [quickSaving, setQuickSaving] = useState(false);
  const [witnessVerifying, setWitnessVerifying] = useState(false);
  // Mobile daily-agenda focus day. Defaults to today when it's in the
  // current week, otherwise the first day of the loaded week. Kept
  // separate from `weekStart` so day switching on phone doesn't reload
  // the whole chart request.
  const [mobileDay, setMobileDay] = useState(() => new Date().toISOString().slice(0, 10));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Per-PRN-card expandable "Recent administrations" state on mobile.
  // Keyed by medication id; presentation-only.
  const [prnHistoryOpen, setPrnHistoryOpen] = useState({});
  // Presentation-only: cache the selected service user's care flags and
  // allergies so the eMAR dialog can surface them without fetching new
  // medication data. Uses the existing read-only client endpoints; does
  // not alter any medication API, payload, or business rule.
  const [clientSafety, setClientSafety] = useState({ care_flags: [], allergies: [], full_name: "" });

  useEffect(() => {
    api.get("/clients").then((r) => {
      setClients(r.data || []);
      if ((r.data || []).length && !clientId) setClientId(r.data[0].id);
    }).catch((e) => toast.error(formatApiError(e)));
  }, [clientId]);

  useEffect(() => {
    if (!clientId) { setClientSafety({ care_flags: [], allergies: [], full_name: "" }); return; }
    let alive = true;
    Promise.all([
      api.get(`/clients/${clientId}/details`).catch(() => ({ data: {} })),
      api.get(`/clients/${clientId}/sections/allergies-diet`).catch(() => ({ data: [] })),
    ]).then(([det, all]) => {
      if (!alive) return;
      setClientSafety({
        care_flags: det.data?.care_flags || [],
        allergies: (all.data || []).filter((a) => (a.active || "yes") !== "no"),
        full_name: det.data?.full_name || "",
      });
    });
    return () => { alive = false; };
  }, [clientId]);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const r = await api.get(`/emar/chart?client_id=${clientId}&week_start=${weekStart}`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [clientId, weekStart]);

  useEffect(() => { load(); }, [load]);

  // Keep the mobile focus day within the currently loaded week; snap to the
  // week start when the user shifts weeks and the previous focus is out of
  // range. Prevents "empty" agenda cards from a stale day pointer.
  useEffect(() => {
    if (!data?.days?.length) return;
    if (!data.days.includes(mobileDay)) {
      const today = new Date().toISOString().slice(0, 10);
      setMobileDay(data.days.includes(today) ? today : data.days[0]);
    }
  }, [data, mobileDay]);

  const eventsFor = (medName, dayIso) => {
    if (!data) return [];
    return data.events.filter(
      (e) => (e.medication_name || "").toLowerCase() === (medName || "").toLowerCase()
        && (e.given_at || "").startsWith(dayIso)
    );
  };

  const doPrint = () => window.print();

  const openQuick = (med, day) => {
    const label = med.name || med.medication_name || med.medication || "Medication";
    const cd = med.is_controlled_drug === true || med.is_controlled_drug === "yes";
    setQuick({
      med: { ...med, label, is_cd: cd },
      day,
      action: "administered",
      reason: "",
      route: med.route || "",
      outcome: "",
      signature_name: user?.name || user?.email || "",
      witness_signature_name: "",
      witness_email: "",
      witness_password: "",
      witness_user_id: "",
      witness_verified: false,
    });
    setQuickOpen(true);
  };

  const openPRN = (med) => {
    const label = med.name || med.medication_name || med.medication || "Medication";
    const cd = med.is_controlled_drug === true || med.is_controlled_drug === "yes";
    const today = new Date().toISOString().slice(0, 10);
    setQuick({
      med: { ...med, label, is_cd: cd },
      day: today,
      action: "prn",
      reason: "",
      route: med.route || "",
      outcome: "",
      signature_name: user?.name || user?.email || "",
      witness_signature_name: "",
      witness_email: "",
      witness_password: "",
      witness_user_id: "",
      witness_verified: false,
    });
    setQuickOpen(true);
  };

  const verifyWitness = async () => {
    if (!quick.witness_email.trim() || !quick.witness_password) {
      toast.error("Enter the witness colleague's email and password");
      return;
    }
    if (quick.witness_email.trim().toLowerCase() === (user?.email || "").toLowerCase()) {
      toast.error("Witness must be a different person from the administering carer");
      return;
    }
    setWitnessVerifying(true);
    try {
      const r = await api.post("/emar/verify-witness", {
        email: quick.witness_email.trim(),
        password: quick.witness_password,
      });
      setQuick((q) => ({
        ...q,
        witness_verified: true,
        witness_user_id: r.data.witness_user_id,
        witness_signature_name: r.data.witness_name,
        witness_password: "",
      }));
      toast.success(`Witness verified · ${r.data.witness_name}`);
    } catch (e) {
      setQuick((q) => ({ ...q, witness_verified: false, witness_user_id: "" }));
      toast.error(formatApiError(e));
    } finally {
      setWitnessVerifying(false);
    }
  };

  const saveQuick = async () => {
    if (!quick.med) return;
    if (!quick.signature_name.trim()) {
      toast.error("Type your name to sign this eMAR entry");
      return;
    }
    if (["refused", "withheld", "prn"].includes(quick.action) && !quick.reason.trim()) {
      toast.error("A reason is required for this action");
      return;
    }
    if (quick.med.is_cd) {
      if (!quick.witness_verified || !quick.witness_user_id) {
        toast.error("Controlled drug — verify the witness colleague before signing");
        return;
      }
      if ((quick.witness_signature_name || "").trim().toLowerCase() === quick.signature_name.trim().toLowerCase()) {
        toast.error("Witness must be a different person from the carer");
        return;
      }
    }
    setQuickSaving(true);
    try {
      const nowTime = new Date();
      const [y, m, d] = quick.day.split("-");
      nowTime.setFullYear(Number(y), Number(m) - 1, Number(d));
      await api.post("/emar", {
        data: {
          client_id: clientId,
          medication_id: quick.med.id,
          medication_name: quick.med.label,
          dose: quick.med.dose || quick.med.dosage || "",
          route: quick.route || quick.med.route || "",
          action: quick.action,
          reason: quick.reason.trim() || null,
          outcome_effectiveness: quick.outcome.trim() || null,
          signature_name: quick.signature_name.trim(),
          witness_signature_name: quick.med.is_cd ? (quick.witness_signature_name || "").trim() : null,
          witness_user_id: quick.med.is_cd ? (quick.witness_user_id || null) : null,
          given_at: nowTime.toISOString(),
        },
      });
      toast.success(`eMAR recorded: ${quick.action}`);
      setQuickOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setQuickSaving(false); }
  };

  const shiftWeek = (n) => setWeekStart(addDays(weekStart, n * 7));
  const shiftDay = (n) => {
    if (!data?.days?.length) return;
    const idx = data.days.indexOf(mobileDay);
    const next = data.days[Math.max(0, Math.min(data.days.length - 1, idx + n))];
    if (next) setMobileDay(next);
  };

  // ── Mobile agenda — groups (medication × scheduled time) by time slot for
  // the currently focused day. Status of each row is derived from the same
  // events array the desktop grid uses, so administration/reason/CD data
  // stays a single source of truth. Business logic is unchanged.
  const parseScheduleTimes = (m) => {
    const raw = m.schedule_times || m.times || m.schedule || "";
    return String(raw)
      .split(/[,;\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
  };
  const mobileAgenda = React.useMemo(() => {
    if (!data || !mobileDay) return { groups: [], count: 0 };
    const rows = [];
    for (const m of data.medications || []) {
      const label = m.name || m.medication_name || m.medication || "Medication";
      const times = parseScheduleTimes(m);
      const isPRN = /prn|as\s*needed/i.test(m.frequency || "");
      if (tab === "prn" ? !isPRN : isPRN) continue;
      if (times.length === 0) {
        rows.push({ med: m, label, time: null });
      } else {
        for (const t of times) rows.push({ med: m, label, time: t });
      }
    }
    // sort by time (null last)
    rows.sort((a, b) => {
      if (!a.time && !b.time) return 0;
      if (!a.time) return 1;
      if (!b.time) return -1;
      return a.time.localeCompare(b.time);
    });
    const groups = {};
    for (const r of rows) {
      const key = r.time || "Anytime";
      (groups[key] ||= []).push(r);
    }
    return { groups: Object.entries(groups), count: rows.length };
  }, [data, mobileDay, tab]);

  const mobileDayLabel = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso + "T00:00:00");
    const today = new Date().toISOString().slice(0, 10);
    const prefix = iso === today ? "Today · " : "";
    return prefix + d.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short" });
  };

  return (
    <div className="flex-1 overflow-y-auto">
      {/* ─── Desktop toolbar (≥ lg) — preserved verbatim ─────────────────── */}
      <div className="hidden lg:flex h-14 border-b border-slate-200 bg-white px-6 items-center justify-between print:hidden">
        <div className="flex items-center gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Clinical</div>
            <h1 className="font-heading text-xl font-semibold" data-testid="mar-title">Weekly MAR Chart</h1>
          </div>
          <div className="flex items-center gap-1 ml-2">
            <button
              onClick={() => setTab("scheduled")}
              data-testid="tab-scheduled"
              className={`h-9 px-3 text-sm border ${tab === "scheduled" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"}`}
            >
              Scheduled
            </button>
            <button
              onClick={() => setTab("prn")}
              data-testid="tab-prn"
              className={`h-9 px-3 text-sm border inline-flex items-center gap-1.5 ${tab === "prn" ? "bg-teal-700 text-white border-teal-700" : "bg-white text-slate-700 border-slate-300"}`}
            >
              <PillBottle className="w-3.5 h-3.5" /> PRN
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger className="h-9 w-56" data-testid="mar-client"><SelectValue placeholder="Service user" /></SelectTrigger>
            <SelectContent>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center border border-slate-300 h-9 bg-white">
            <button onClick={() => shiftWeek(-1)} data-testid="mar-prev-week" className="px-2 hover:bg-slate-50"><ChevronLeft className="w-4 h-4" /></button>
            <div className="px-2 text-xs flex items-center gap-1.5 border-x border-slate-300 h-full">
              <CalendarDays className="w-3.5 h-3.5 text-slate-500" />
              <input type="date" value={weekStart} onChange={(e) => setWeekStart(mondayOf(e.target.value))} data-testid="mar-week-start" className="text-sm outline-none" />
            </div>
            <button onClick={() => shiftWeek(1)} data-testid="mar-next-week" className="px-2 hover:bg-slate-50"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <Button size="sm" onClick={doPrint} data-testid="btn-print-mar" className="h-9 bg-slate-900 hover:bg-slate-800">
            <Printer className="w-3.5 h-3.5 mr-1.5" /> Print
          </Button>
          <Button
            size="sm" variant="outline"
            onClick={async () => {
              const t = toast.loading("Preparing PDF…");
              try {
                await downloadBlob(`/emar/chart/pdf?client_id=${clientId}&week_start=${weekStart}`, `mar-chart-${weekStart}.pdf`);
                toast.success("MAR PDF downloaded", { id: t });
              } catch (e) { toast.error(formatApiError(e), { id: t }); }
            }}
            data-testid="btn-download-mar-pdf" className="h-9"
          >
            <FileDown className="w-3.5 h-3.5 mr-1.5" /> Download PDF
          </Button>
        </div>
      </div>

      {/* ─── Mobile / tablet toolbar (< lg) ─────────────────────────────── */}
      <div className="lg:hidden border-b border-slate-200 bg-white px-4 py-3 space-y-2 print:hidden" data-testid="mar-mobile-toolbar">
        {/* Service user row + more menu */}
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Service user</div>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="h-11 w-full text-sm mt-0.5" data-testid="mar-client-mobile"><SelectValue placeholder="Choose a service user" /></SelectTrigger>
              <SelectContent>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="relative">
            <button
              type="button"
              onClick={() => setMobileMenuOpen((o) => !o)}
              data-testid="mar-mobile-more"
              aria-label="More MAR actions"
              className="w-11 h-11 inline-flex items-center justify-center rounded-md text-slate-700 hover:bg-slate-100 mt-4 border border-slate-200"
            >
              <MoreVertical className="w-5 h-5" />
            </button>
            {mobileMenuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMobileMenuOpen(false)} />
                <div className="absolute right-0 top-full mt-1 z-50 min-w-[200px] bg-white border border-slate-200 shadow-lg rounded-md overflow-hidden" data-testid="mar-mobile-more-menu">
                  <button
                    onClick={() => { setMobileMenuOpen(false); doPrint(); }}
                    data-testid="mar-mobile-print"
                    className="w-full flex items-center gap-2 px-3 py-3 text-sm text-slate-700 hover:bg-slate-50"
                  ><Printer className="w-4 h-4" /> Print MAR</button>
                  <button
                    onClick={async () => {
                      setMobileMenuOpen(false);
                      const t = toast.loading("Preparing PDF…");
                      try {
                        await downloadBlob(`/emar/chart/pdf?client_id=${clientId}&week_start=${weekStart}`, `mar-chart-${weekStart}.pdf`);
                        toast.success("MAR PDF downloaded", { id: t });
                      } catch (e) { toast.error(formatApiError(e), { id: t }); }
                    }}
                    data-testid="mar-mobile-pdf"
                    className="w-full flex items-center gap-2 px-3 py-3 text-sm text-slate-700 hover:bg-slate-50 border-t border-slate-100"
                  ><FileDown className="w-4 h-4" /> Download PDF</button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Date navigator */}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-0.5">Date</div>
          <MobileAgendaView
            label={mobileDayLabel(mobileDay)}
            onPrev={data?.days?.length ? () => shiftDay(-1) : null}
            onNext={data?.days?.length ? () => shiftDay(1) : null}
            onToday={() => {
              const today = new Date().toISOString().slice(0, 10);
              setWeekStart(mondayOf(today));
              setMobileDay(today);
            }}
            isToday={mobileDay === new Date().toISOString().slice(0, 10)}
            testid="mar-day-nav"
          />
        </div>

        {/* Scheduled / PRN tabs — full width, text + icon */}
        <div className="grid grid-cols-2 gap-2" role="tablist" aria-label="Medication view">
          <button
            role="tab"
            onClick={() => setTab("scheduled")}
            data-testid="tab-scheduled-mobile"
            aria-selected={tab === "scheduled"}
            className={`h-11 inline-flex items-center justify-center gap-1.5 text-sm font-medium border rounded-md ${
              tab === "scheduled"
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-300"
            }`}
          >
            <CalendarDays className="w-4 h-4" /> Scheduled
          </button>
          <button
            role="tab"
            onClick={() => setTab("prn")}
            data-testid="tab-prn-mobile"
            aria-selected={tab === "prn"}
            className={`h-11 inline-flex items-center justify-center gap-1.5 text-sm font-medium border rounded-md ${
              tab === "prn"
                ? "bg-teal-700 text-white border-teal-700"
                : "bg-white text-slate-700 border-slate-300"
            }`}
          >
            <PillBottle className="w-4 h-4" /> PRN
          </button>
        </div>
      </div>

      {/* ─── Mobile daily agenda (< lg) ───────────────────────────────── */}
      <div className="lg:hidden p-4 space-y-3" data-testid="mar-mobile-agenda-wrap">
        {loading || !data ? (
          <div className="text-sm text-slate-500" data-testid="mar-mobile-loading">Loading chart…</div>
        ) : tab === "prn" ? (
          (() => {
            const prnMeds = (data.medications || []).filter((m) => /prn|as\s*needed/i.test(m.frequency || ""));
            const prnEvents = (data.events || []).filter((e) => e.action === "prn").sort((a, b) => (b.given_at || "").localeCompare(a.given_at || ""));
            if (!prnMeds.length) return (
              <div className="bg-white border border-slate-200 rounded-lg p-6 text-center text-sm text-slate-500" data-testid="prn-empty-mobile">
                No PRN medications recorded for this service user.
              </div>
            );
            return (
              <>
                <ul className="space-y-2" data-testid="prn-cards-mobile">
                  {prnMeds.map((m) => {
                    const label = m.name || m.medication_name || m.medication || "Medication";
                    const dose = m.dose || m.dosage || "";
                    const isCD = m.is_controlled_drug === true || m.is_controlled_drug === "yes";
                    const medHistory = prnEvents.filter((e) => (e.medication_name || "").toLowerCase() === label.toLowerCase());
                    const lastPrn = medHistory[0];
                    const lastElapsed = lastPrn ? elapsedSince(lastPrn.given_at) : null;
                    const interval = Number(m.min_interval_hours) || 0;
                    let locked = false, waitMin = 0;
                    if (interval > 0 && lastPrn?.given_at) {
                      const next = new Date(new Date(lastPrn.given_at).getTime() + interval * 3600 * 1000);
                      if (next.getTime() > Date.now()) { locked = true; waitMin = Math.ceil((next.getTime() - Date.now()) / 60000); }
                    }
                    const historyOpen = !!prnHistoryOpen[m.id];
                    const maxDose = m.max_daily_dose || m.max_frequency || m.max_dose_24h || null;
                    return (
                      <li key={m.id} data-testid={`prn-card-mobile-${m.id}`} className="bg-white border border-slate-200 rounded-lg p-4 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-semibold text-slate-900 break-words">{label}</div>
                            {(dose || m.route) && <div className="text-xs text-slate-600 mt-0.5">{dose}{m.route ? ` · ${m.route}` : ""}</div>}
                            {interval > 0 && <div className="text-[11px] text-slate-500 mt-0.5">Min interval: {interval}h between doses</div>}
                            {maxDose && <div className="text-[11px] text-slate-500">Max: {maxDose}</div>}
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-teal-300 bg-teal-50 text-teal-800 rounded font-semibold">
                              <PillBottle className="w-3 h-3" aria-hidden="true" /> PRN
                            </span>
                            {isCD && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-red-300 bg-red-50 text-red-800 rounded font-semibold" data-testid={`prn-card-cd-${m.id}`}>
                              <ShieldAlert className="w-3 h-3" aria-hidden="true" /> CD
                            </span>}
                          </div>
                        </div>
                        {m.notes && (
                          <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 p-2 rounded">
                            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 flex items-center gap-1"><Info className="w-3 h-3" /> Indication / instructions</div>
                            <div className="whitespace-pre-wrap break-words">{m.notes}</div>
                          </div>
                        )}
                        {lastPrn ? (
                          <div className="text-xs bg-sky-50/60 border border-sky-200 rounded p-2 space-y-0.5" data-testid={`prn-last-mobile-${m.id}`}>
                            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-sky-800 font-semibold">
                              <CheckCircle2 className="w-3 h-3" aria-hidden="true" /> Last administered
                            </div>
                            <div className="text-slate-800">
                              {new Date(lastPrn.given_at).toLocaleString()} · <span className="text-slate-600">{lastElapsed}</span>
                            </div>
                            {lastPrn.reason && <div className="text-slate-700"><span className="text-slate-500">Reason:</span> {lastPrn.reason}</div>}
                            <div className="text-[11px] text-slate-500">By {lastPrn.signature_name || lastPrn.created_by_name}</div>
                          </div>
                        ) : (
                          <div className="text-[11px] text-slate-500 italic">No PRN administrations recorded yet for this medication.</div>
                        )}
                        {locked && (
                          <div className="text-xs bg-amber-50 border border-amber-300 text-amber-900 p-2 rounded flex items-start gap-1.5" data-testid={`prn-locked-mobile-${m.id}`}>
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> <span>Locked — wait ~{waitMin} more min before signing another dose.</span>
                          </div>
                        )}
                        <Button
                          size="sm"
                          onClick={() => openPRN(m)}
                          disabled={locked}
                          data-testid={`btn-sign-prn-mobile-${m.id}`}
                          className={`h-11 w-full ${locked ? "bg-slate-300 text-slate-500 cursor-not-allowed" : "bg-teal-600 hover:bg-teal-700 text-white"}`}
                        >
                          <PillBottle className="w-4 h-4 mr-1.5" /> {locked ? "Locked — too soon" : "Sign PRN Medication"}
                        </Button>
                        {medHistory.length > 0 && (
                          <div className="border-t border-slate-100 pt-2">
                            <button
                              type="button"
                              onClick={() => setPrnHistoryOpen((s) => ({ ...s, [m.id]: !s[m.id] }))}
                              data-testid={`prn-history-toggle-${m.id}`}
                              aria-expanded={historyOpen}
                              className="w-full h-11 inline-flex items-center justify-between px-1 text-sm font-medium text-slate-700 hover:text-slate-900"
                            >
                              <span className="inline-flex items-center gap-1.5">
                                <CalendarDays className="w-4 h-4 text-slate-500" aria-hidden="true" />
                                Recent administrations
                                <span className="text-[11px] text-slate-500 font-normal">({medHistory.length})</span>
                              </span>
                              <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform ${historyOpen ? "rotate-90" : ""}`} aria-hidden="true" />
                            </button>
                            {historyOpen && (
                              <ul className="mt-1 space-y-1.5" data-testid={`prn-history-list-${m.id}`}>
                                {medHistory.slice(0, 10).map((e) => {
                                  const meta = ACTION_META[e.action] || ACTION_META.prn;
                                  const StatusIcon = meta.Icon;
                                  return (
                                    <li key={e.id} className="text-xs bg-slate-50 border border-slate-200 rounded p-2 space-y-0.5" data-testid={`prn-history-item-${e.id}`}>
                                      <div className="flex items-center gap-1.5 text-slate-800">
                                        <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} aria-hidden="true" />
                                        <StatusIcon className={`w-3.5 h-3.5 ${meta.text}`} aria-hidden="true" />
                                        <span className={`font-semibold ${meta.text}`}>{meta.label}</span>
                                        <span className="text-slate-500 font-normal">· {new Date(e.given_at).toLocaleString()}</span>
                                      </div>
                                      {e.reason && <div className="text-slate-700"><span className="text-slate-500">Reason:</span> {e.reason}</div>}
                                      {e.outcome_effectiveness && <div className="text-slate-700"><span className="text-slate-500">Outcome:</span> {e.outcome_effectiveness}</div>}
                                      <div className="text-[11px] text-slate-500">Signed by {e.signature_name || e.created_by_name}</div>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
                {prnEvents.length > 0 && (
                  <ul className="mt-3 space-y-2" data-testid="prn-history-mobile">
                    <li className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold px-1">All recent PRN administrations (this week)</li>
                    {prnEvents.slice(0, 8).map((e) => (
                      <li key={e.id} className="bg-white border border-slate-200 rounded-lg p-3 text-xs" data-testid={`prn-history-mobile-${e.id}`}>
                        <div className="font-medium text-slate-900">{e.medication_name}</div>
                        <div className="text-slate-600 mt-0.5">{new Date(e.given_at).toLocaleString()} · {e.signature_name || e.created_by_name}</div>
                        {e.reason && <div className="text-slate-700 mt-1"><span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Reason:</span> {e.reason}</div>}
                        {e.outcome_effectiveness && <div className="text-slate-700 mt-1"><span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Outcome:</span> {e.outcome_effectiveness}</div>}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            );
          })()
        ) : mobileAgenda.count === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-6 text-center text-sm text-slate-500" data-testid="mar-empty-mobile">
            No scheduled medications for this service user.
          </div>
        ) : (
          <ul className="space-y-3" data-testid="mar-agenda-list">
            {mobileAgenda.groups.map(([time, rows]) => (
              <li key={time} data-testid={`mar-agenda-slot-${time}`}>
                <div className="flex items-center gap-2 px-1 mb-1.5">
                  <div className="text-[13px] font-heading font-semibold text-slate-900 tabular-nums">{time}</div>
                  <div className="h-px flex-1 bg-slate-200" />
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{rows.length} med{rows.length === 1 ? "" : "s"}</div>
                </div>
                <ul className="space-y-2">
                  {rows.map(({ med, label }) => {
                    const evs = eventsFor(label, mobileDay);
                    const isCD = med.is_controlled_drug === true || med.is_controlled_drug === "yes";
                    const dose = med.dose || med.dosage || "";
                    const latest = evs.length ? evs[evs.length - 1] : null;
                    const meta = latest ? ACTION_META[latest.action] || ACTION_META.administered : null;
                    const StatusIcon = latest ? meta.Icon : Circle;
                    return (
                      <li key={`${med.id}-${time}`} data-testid={`mar-agenda-card-${med.id}-${time}`} className="bg-white border border-slate-200 rounded-lg p-4 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-semibold text-slate-900 break-words">{label}</div>
                            <div className="text-xs text-slate-600 mt-0.5">
                              {dose}{med.route ? ` · ${med.route}` : ""}
                              {med.frequency ? <> · <span className="text-slate-500">{med.frequency}</span></> : null}
                            </div>
                          </div>
                          {isCD && <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-red-300 bg-red-50 text-red-800 rounded font-semibold shrink-0" data-testid={`mar-agenda-cd-${med.id}`}>CD</span>}
                        </div>
                        {med.notes && (
                          <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 p-2 rounded flex items-start gap-1.5">
                            <Info className="w-3 h-3 shrink-0 mt-0.5 text-slate-500" />
                            <span className="whitespace-pre-wrap break-words">{med.notes}</span>
                          </div>
                        )}
                        {latest ? (
                          <div
                            className={`flex items-center gap-1.5 text-[13px] font-semibold ${meta.text}`}
                            data-testid={`mar-agenda-status-${med.id}-${time}`}
                            aria-label={`Status: ${meta.label} at ${new Date(latest.given_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`}
                          >
                            <span className={`w-2 h-2 rounded-full ${meta.dot}`} aria-hidden="true" />
                            <StatusIcon className="w-4 h-4" aria-hidden="true" />
                            <span>{meta.label}</span>
                            <span className="text-slate-500 font-normal">· {new Date(latest.given_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</span>
                            {latest.reason && <span className="text-slate-500 font-normal truncate">· {latest.reason}</span>}
                          </div>
                        ) : (
                          <div
                            className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-700"
                            data-testid={`mar-agenda-due-${med.id}-${time}`}
                            aria-label="Status: Due — not yet administered"
                          >
                            <span className="w-2 h-2 rounded-full bg-slate-400" aria-hidden="true" />
                            <Circle className="w-4 h-4 text-slate-500" aria-hidden="true" strokeWidth={2.5} />
                            <span>Due</span>
                          </div>
                        )}
                        {evs.length > 1 && (
                          <div className="text-[11px] text-slate-500" data-testid={`mar-agenda-count-${med.id}-${time}`}>
                            {evs.length} entries today — tap Record eMAR to view or add another
                          </div>
                        )}
                        <Button
                          size="sm"
                          onClick={() => openQuick(med, mobileDay)}
                          data-testid={`btn-record-emar-${med.id}-${time}`}
                          className="h-11 w-full bg-sky-700 hover:bg-sky-800 text-white"
                        >
                          <Plus className="w-4 h-4 mr-1.5" /> Record eMAR
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
            {/* Legend — always shows text with icon + colour so status never depends on colour alone */}
            <li className="mt-3 border-t border-slate-200 pt-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Status legend</div>
              <div className="flex flex-wrap gap-2 text-[11px]">
                {["administered","refused","withheld","prn"].map((k) => {
                  const m = ACTION_META[k]; const I = m.Icon;
                  return (
                    <span key={k} className={`inline-flex items-center gap-1 px-2 py-0.5 border rounded-full ${m.ring} ${m.text} bg-white`} data-testid={`mar-legend-${k}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} aria-hidden="true" />
                      <I className="w-3 h-3" aria-hidden="true" />
                      {m.label}
                    </span>
                  );
                })}
                <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-slate-300 rounded-full text-slate-700 bg-white">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400" aria-hidden="true" />
                  <Circle className="w-3 h-3" aria-hidden="true" strokeWidth={2.5} />
                  Due
                </span>
              </div>
            </li>
          </ul>
        )}
      </div>

      {/* ─── Desktop weekly grid (≥ lg) — preserved verbatim ─────────────── */}
      <div className="hidden lg:block p-6" ref={printRef}>
        <div className="mb-4 hidden print:block">
          <div className="font-heading text-2xl font-semibold">Weekly MAR Chart</div>
          <div className="text-sm text-slate-700">{data?.client?.full_name} · Week beginning {weekStart}</div>
        </div>

        {loading || !data ? (
          <div className="text-sm text-slate-500" data-testid="mar-loading">Loading chart…</div>
        ) : tab === "prn" ? (
          (() => {
            const prnMeds = (data.medications || []).filter(
              (m) => (m.frequency || "").toLowerCase().includes("prn") || (m.status || "active") === "active" && ((m.frequency || "").toLowerCase().includes("as needed") || (m.frequency || "").toLowerCase().includes("prn"))
            );
            const prnEvents = (data.events || []).filter((e) => e.action === "prn").sort((a, b) => (b.given_at || "").localeCompare(a.given_at || ""));
            if (!prnMeds.length) {
              return (
                <div className="bg-white border border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="prn-empty">
                  No PRN medications recorded for this service user. Set a medication&apos;s frequency to <span className="font-semibold">PRN (as needed)</span> in their profile to sign it here.
                </div>
              );
            }
            return (
              <div className="space-y-4" data-testid="prn-view">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="prn-cards">
                  {prnMeds.map((m) => {
                    const label = m.name || m.medication_name || m.medication || "Medication";
                    const dose = m.dose || m.dosage || "";
                    const lastPrn = prnEvents.find((e) => (e.medication_name || "").toLowerCase() === label.toLowerCase());
                    const interval = Number(m.min_interval_hours) || 0;
                    let nextAvailable = null;
                    let waitMinutes = 0;
                    if (interval > 0 && lastPrn?.given_at) {
                      const last = new Date(lastPrn.given_at);
                      const next = new Date(last.getTime() + interval * 3600 * 1000);
                      if (next.getTime() > Date.now()) {
                        nextAvailable = next;
                        waitMinutes = Math.ceil((next.getTime() - Date.now()) / 60000);
                      }
                    }
                    const locked = !!nextAvailable;
                    return (
                      <div key={m.id} data-testid={`prn-card-${m.id}`} className="bg-white border border-slate-200 p-4 flex flex-col gap-3">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="font-heading font-semibold text-slate-900" data-testid={`prn-name-${m.id}`}>{label}</div>
                            {dose && <div className="text-xs text-slate-600 mt-0.5">Dose: <span className="font-medium">{dose}</span></div>}
                            {interval > 0 && (
                              <div className="text-[11px] text-slate-500 mt-0.5" data-testid={`prn-interval-${m.id}`}>Min interval: {interval}h between doses</div>
                            )}
                          </div>
                          <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-teal-300 bg-teal-50 text-teal-800 shrink-0">PRN</span>
                        </div>
                        {m.notes && (
                          <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 p-2">
                            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 flex items-center gap-1"><Info className="w-3 h-3" /> Instructions</div>
                            <div className="whitespace-pre-wrap">{m.notes}</div>
                          </div>
                        )}
                        {lastPrn && (
                          <div className="text-[11px] text-slate-500" data-testid={`prn-last-${m.id}`}>
                            Last given {new Date(lastPrn.given_at).toLocaleString()} · {lastPrn.reason || "no reason recorded"} · by {lastPrn.signature_name || lastPrn.created_by_name}
                          </div>
                        )}
                        {locked && (
                          <div className="text-xs bg-amber-50 border border-amber-300 text-amber-900 p-2" data-testid={`prn-locked-${m.id}`}>
                            Next dose available at <span className="font-semibold tabular-nums" data-testid={`prn-next-time-${m.id}`}>{nextAvailable.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</span>
                            <span className="text-[11px] block mt-0.5">Wait ~{waitMinutes} more min before signing another dose.</span>
                          </div>
                        )}
                        {m.is_controlled_drug === true || m.is_controlled_drug === "yes" ? (
                          <div className="text-[10px] uppercase tracking-widest text-red-700 font-semibold">Controlled drug — witness required</div>
                        ) : null}
                        <Button
                          size="sm"
                          onClick={() => openPRN(m)}
                          disabled={locked}
                          data-testid={`btn-sign-prn-${m.id}`}
                          className={`h-10 ${locked ? "bg-slate-300 text-slate-500 cursor-not-allowed" : "bg-teal-600 hover:bg-teal-700 text-white"}`}
                        >
                          {locked ? "Locked — too soon" : "Sign PRN Medication"}
                        </Button>
                      </div>
                    );
                  })}
                </div>
                {prnEvents.length > 0 && (
                  <div className="bg-white border border-slate-200" data-testid="prn-history">
                    <div className="px-3 py-2 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">PRN administration history · week of {weekStart}</div>
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-100 bg-slate-50 text-xs text-slate-700">
                          <th className="text-left px-3 py-2 font-semibold">When</th>
                          <th className="text-left px-3 py-2 font-semibold">Medication</th>
                          <th className="text-left px-3 py-2 font-semibold">Reason</th>
                          <th className="text-left px-3 py-2 font-semibold">Signed by</th>
                        </tr>
                      </thead>
                      <tbody>
                        {prnEvents.map((e) => (
                          <tr key={e.id} className="border-b border-slate-100" data-testid={`prn-history-${e.id}`}>
                            <td className="px-3 py-2 tabular-nums text-xs">{new Date(e.given_at).toLocaleString()}</td>
                            <td className="px-3 py-2">{e.medication_name}</td>
                            <td className="px-3 py-2 text-xs text-slate-700">{e.reason || "—"}</td>
                            <td className="px-3 py-2 text-xs">{e.signature_name || e.created_by_name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })()
        ) : data.medications.length === 0 ? (
          <div className="bg-white border border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="mar-empty">
            No medications recorded for this service user.
          </div>
        ) : (
          <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="mar-grid">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left px-3 py-2 font-semibold text-slate-700 w-56">Medication</th>
                  {data.days.map((d) => {
                    const dt = new Date(d + "T00:00:00");
                    return (
                      <th key={d} className="px-2 py-2 text-center text-xs font-semibold text-slate-700">
                        <div>{dt.toLocaleDateString(undefined, { weekday: "short" })}</div>
                        <div className="text-[11px] text-slate-500 font-normal">{dt.toLocaleDateString(undefined, { day: "2-digit", month: "short" })}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {data.medications.map((m) => {
                  const label = m.name || m.medication_name || m.medication || "Medication";
                  return (
                    <tr key={m.id} className="border-b border-slate-100" data-testid={`mar-row-${m.id}`}>
                      <td className="px-3 py-2 align-top">
                        <div className="font-medium text-slate-900">{label}</div>
                        {(m.dose || m.dosage) && <div className="text-[11px] text-slate-500">{m.dose || m.dosage}</div>}
                        {m.frequency && <div className="text-[11px] text-slate-500">{m.frequency}</div>}
                      </td>
                      {data.days.map((d) => {
                        const evs = eventsFor(label, d);
                        return (
                          <td key={d} className="px-2 py-2 align-top text-center">
                            {evs.length === 0 ? (
                              <button
                                onClick={() => openQuick(m, d)}
                                data-testid={`mar-empty-${m.id}-${d}`}
                                className="text-slate-300 hover:text-sky-700 text-xs w-full h-8 border border-transparent hover:border-sky-300 hover:bg-sky-50 print:hover:bg-transparent print:hover:border-transparent"
                                title="Record eMAR"
                              >
                                <Plus className="w-3 h-3 inline-block" />
                              </button>
                            ) : (
                              <div className="flex flex-col gap-1 items-center">
                                {evs.map((ev) => (
                                  <span
                                    key={ev.id}
                                    data-testid={`mar-cell-${m.id}-${d}`}
                                    title={`${ev.action}${ev.reason ? " · " + ev.reason : ""} · ${new Date(ev.given_at).toLocaleTimeString()} · ${ev.signature_name || ev.created_by_name || ""}`}
                                    className={`inline-block text-[10px] uppercase tracking-widest border px-1.5 py-0.5 ${ACTION_STYLE[ev.action] || "bg-slate-100 border-slate-300"}`}
                                  >
                                    {ACTION_SHORT[ev.action] || ev.action}
                                  </span>
                                ))}
                                <button
                                  onClick={() => openQuick(m, d)}
                                  data-testid={`mar-add-${m.id}-${d}`}
                                  className="text-[10px] text-slate-400 hover:text-sky-700 print:hidden"
                                  title="Add another entry"
                                >
                                  <Plus className="w-3 h-3 inline-block" />
                                </button>
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="text-[11px] text-slate-500 px-3 py-2 border-t border-slate-100 flex flex-wrap gap-3">
              <span><span className="inline-block w-3 h-3 align-middle mr-1 bg-emerald-100 border border-emerald-300" /> Administered</span>
              <span><span className="inline-block w-3 h-3 align-middle mr-1 bg-amber-100 border border-amber-300" /> Refused</span>
              <span><span className="inline-block w-3 h-3 align-middle mr-1 bg-slate-200 border border-slate-300" /> Withheld</span>
              <span><span className="inline-block w-3 h-3 align-middle mr-1 bg-sky-100 border border-sky-300" /> PRN</span>
            </div>
          </div>
        )}
      </div>

      <Dialog open={quickOpen} onOpenChange={setQuickOpen}>
        <DialogContent data-testid="quick-emar-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              Record eMAR
              {quick.med?.is_cd && (
                <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-red-300 bg-red-50 text-red-800" data-testid="cd-badge">Controlled drug</span>
              )}
            </DialogTitle>
            <DialogDescription>{quick.med?.label}{quick.med?.dose ? ` · ${quick.med.dose}` : ""} · {quick.day}</DialogDescription>
          </DialogHeader>

          {/* Service-user safety strip — sourced from already-loaded client
              details (care flags + allergies-diet). Uses text + icon + colour
              so colour is never the sole signal. Non-dismissible; closes with
              the dialog only. */}
          {(clientSafety.care_flags?.length > 0 || clientSafety.allergies?.length > 0) && (
            <div
              className="border border-red-300 bg-red-50/70 p-3 space-y-2"
              data-testid="quick-emar-safety-strip"
              role="alert"
              aria-label="Service user safety information"
            >
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold text-red-800">
                <ShieldAlert className="w-3.5 h-3.5" aria-hidden="true" />
                Service-user safety · {clientSafety.full_name || "current person"}
              </div>
              {clientSafety.care_flags?.length > 0 && (
                <div className="flex flex-wrap gap-1" data-testid="safety-care-flags">
                  {clientSafety.care_flags.map((f) => (
                    <span
                      key={f}
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full border border-red-400 bg-white text-red-900 font-medium"
                      data-testid={`safety-flag-${f}`}
                    >
                      <AlertTriangle className="w-3 h-3" aria-hidden="true" />
                      {String(f).replace(/_/g, " ").toUpperCase()}
                    </span>
                  ))}
                </div>
              )}
              {clientSafety.allergies?.length > 0 && (
                <div className="flex flex-wrap gap-1" data-testid="safety-allergies">
                  {clientSafety.allergies.slice(0, 8).map((a) => {
                    const sev = getAllergySeverity(a.item || a);
                    return (
                      <span
                        key={a.id || a.item || a}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full border font-medium ${ALLERGY_SEVERITY_STYLES[sev]}`}
                        title={a.reaction || undefined}
                        data-testid={`safety-allergy-${a.id || a.item}`}
                      >
                        <AlertTriangle className="w-3 h-3" aria-hidden="true" />
                        <span className="uppercase tracking-widest text-[9px] font-bold mr-0.5">{sev}</span>
                        {a.item || a}
                      </span>
                    );
                  })}
                  {clientSafety.allergies.length > 8 && (
                    <span className="text-[11px] text-slate-600">+{clientSafety.allergies.length - 8} more</span>
                  )}
                </div>
              )}
              <div className="text-[11px] text-red-900/80">
                Check the person&apos;s care plan before administering. This summary is informational; it does not replace clinical judgement.
              </div>
            </div>
          )}

          {quick.med?.is_cd && (
            <div className="border border-red-300 bg-red-50 text-red-900 p-3 text-xs" data-testid="cd-banner">
              <div className="font-semibold uppercase tracking-widest text-[10px] mb-1 flex items-center gap-1">
                <ShieldAlert className="w-3.5 h-3.5" aria-hidden="true" /> Second-signatory required
              </div>
              This medication is a controlled drug (CD). An authorised colleague must sign in below as your witness before this administration can be recorded. Both signatures are stored in the CD ledger and audit trail.
            </div>
          )}

          {/* Presentation-only: surface the last recorded eMAR for this
              medication before signing so the carer sees prior context.
              Derived from `data.events` — no new API, no new business rule. */}
          {quick.med && (() => {
            const lbl = (quick.med.label || quick.med.name || quick.med.medication_name || "").toLowerCase();
            const prior = (data?.events || [])
              .filter((e) => (e.medication_name || "").toLowerCase() === lbl)
              .sort((a, b) => (b.given_at || "").localeCompare(a.given_at || ""));
            if (!prior.length) return null;
            const last = prior[0];
            const meta = ACTION_META[last.action] || ACTION_META.administered;
            const StatusIcon = meta.Icon;
            return (
              <div className="border border-slate-200 bg-slate-50 rounded p-2 text-xs space-y-0.5" data-testid="quick-emar-last-admin">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                  <CalendarDays className="w-3 h-3" aria-hidden="true" /> Last recorded for this medication
                </div>
                <div className="flex items-center gap-1.5 text-slate-800">
                  <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} aria-hidden="true" />
                  <StatusIcon className={`w-3.5 h-3.5 ${meta.text}`} aria-hidden="true" />
                  <span className={`font-semibold ${meta.text}`}>{meta.label}</span>
                  <span className="text-slate-500 font-normal">· {new Date(last.given_at).toLocaleString()}</span>
                  <span className="text-slate-500 font-normal">· {elapsedSince(last.given_at)}</span>
                </div>
                {last.reason && <div className="text-slate-700"><span className="text-slate-500">Reason:</span> {last.reason}</div>}
                {last.outcome_effectiveness && <div className="text-slate-700"><span className="text-slate-500">Outcome:</span> {last.outcome_effectiveness}</div>}
                <div className="text-[11px] text-slate-500">Signed by {last.signature_name || last.created_by_name}</div>
              </div>
            );
          })()}
          <div className="space-y-3">
            <div>
              <Label>Action</Label>
              {/* Retain existing <Select>; add icon + colour dot inside each
                  option so users get text + icon + colour signal. Enum values
                  stay identical so the backend payload is unchanged. */}
              <Select value={quick.action} onValueChange={(v) => setQuick({ ...quick, action: v })}>
                <SelectTrigger data-testid="quick-emar-action" className="h-11 md:h-10">
                  <SelectValue>
                    {(() => {
                      const m = ACTION_META[quick.action] || ACTION_META.administered;
                      const Icon = m.Icon;
                      return (
                        <span className="inline-flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${m.dot}`} aria-hidden="true" />
                          <Icon className={`w-4 h-4 ${m.text}`} aria-hidden="true" />
                          <span className={`font-medium ${m.text}`}>{m.label}</span>
                        </span>
                      );
                    })()}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {["administered", "refused", "withheld", "prn"].map((k) => {
                    const m = ACTION_META[k];
                    const Icon = m.Icon;
                    return (
                      <SelectItem key={k} value={k} data-testid={`quick-emar-action-${k}`}>
                        <span className="inline-flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${m.dot}`} aria-hidden="true" />
                          <Icon className={`w-4 h-4 ${m.text}`} aria-hidden="true" />
                          <span className={m.text}>{m.label}</span>
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Route</Label>
                <Input value={quick.route} onChange={(e) => setQuick({ ...quick, route: e.target.value })} placeholder="e.g. Oral" data-testid="quick-emar-route" className="h-11 md:h-10" />
              </div>
              <div>
                <Label>Dose</Label>
                <Input value={quick.med?.dose || ""} readOnly className="bg-slate-50 text-slate-600 h-11 md:h-10" data-testid="quick-emar-dose" />
              </div>
            </div>
            {["refused", "withheld", "prn"].includes(quick.action) && (
              <div>
                <Label>{quick.action === "prn" ? "PRN reason / indication" : "Reason"}</Label>
                <Textarea rows={2} value={quick.reason} onChange={(e) => setQuick({ ...quick, reason: e.target.value })} placeholder={quick.action === "prn" ? "e.g. Complaining of pain, headache since 09:30" : "Reason"} data-testid="quick-emar-reason" />
              </div>
            )}
            {quick.action === "prn" && (
              <div>
                <Label>Outcome / effectiveness (optional)</Label>
                <Textarea rows={2} value={quick.outcome} onChange={(e) => setQuick({ ...quick, outcome: e.target.value })} placeholder="e.g. Pain settled within 30 min, no further symptoms" data-testid="quick-emar-outcome" />
              </div>
            )}
            <div>
              <Label>Your signature (type your name)</Label>
              <Input value={quick.signature_name} onChange={(e) => setQuick({ ...quick, signature_name: e.target.value })} placeholder="Type your full name to sign" data-testid="quick-emar-signature" className="h-11 md:h-10" />
            </div>
            {quick.med?.is_cd && (
              <div className="border border-slate-300 bg-slate-50 p-3 space-y-2" data-testid="quick-emar-cd">
                <div className="text-[10px] uppercase tracking-widest text-red-700 font-semibold">Witness authentication</div>
                {quick.witness_verified ? (
                  <div className="bg-emerald-50 border border-emerald-300 text-emerald-900 p-2 text-xs flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between" data-testid="witness-verified">
                    <div>
                      <div className="font-semibold">{quick.witness_signature_name}</div>
                      <div className="text-[11px] text-emerald-700">Verified as controlled-drug witness</div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-11 md:h-7 text-xs w-full sm:w-auto"
                      onClick={() => setQuick((q) => ({ ...q, witness_verified: false, witness_user_id: "", witness_password: "" }))}
                      data-testid="witness-change"
                    >Change witness</Button>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 gap-2">
                      <Input value={quick.witness_email} onChange={(e) => setQuick({ ...quick, witness_email: e.target.value })} placeholder="Witness email" type="email" autoComplete="off" data-testid="witness-email" className="h-11 md:h-10" />
                      <Input value={quick.witness_password} onChange={(e) => setQuick({ ...quick, witness_password: e.target.value })} placeholder="Witness password" type="password" autoComplete="off" data-testid="witness-password" className="h-11 md:h-10" />
                    </div>
                    <Button size="sm" variant="outline" onClick={verifyWitness} disabled={witnessVerifying} data-testid="btn-verify-witness" className="h-11 md:h-8 w-full">
                      {witnessVerifying ? "Verifying…" : "Verify witness"}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setQuickOpen(false)}
              className="h-11 md:h-10 w-full sm:w-auto"
              data-testid="btn-quick-emar-cancel"
            >Cancel</Button>
            <Button
              onClick={saveQuick}
              disabled={quickSaving || (quick.med?.is_cd && !quick.witness_verified)}
              data-testid="btn-quick-emar-save"
              className="bg-slate-900 hover:bg-slate-800 h-11 md:h-10 w-full sm:w-auto inline-flex items-center gap-2"
            >
              {(() => {
                const m = ACTION_META[quick.action] || ACTION_META.administered;
                const Icon = m.Icon;
                return (
                  <>
                    <Icon className="w-4 h-4" aria-hidden="true" />
                    <span>{quickSaving ? "Signing…" : `Sign ${m.label}`}</span>
                  </>
                );
              })()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
